/**
 * The step editor, fully wired. Three modes on one route:
 *  - create ("new"): name + trigger, then steps; Save creates (born disabled)
 *  - composed: edit the draft; Test dry-runs the DRAFT against a real event.
 *    Enable is ungated here — the server checks the definition is runnable
 *    and answers with a plain list of what's still missing.
 *  - system: read-only catalog steps + the config slot (escalation approvers)
 *    and the kill switch — everything the retired Workflows page did
 */
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  Input,
  Modal,
  Select,
} from '@servicecompressionllc/sc-design-system';
import {
  ArrowLeft,
  Check,
  CircleAlert,
  Pencil,
} from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { Toggle } from '../../components/Toggle.js';
import type { ApproverRef, HitlRouteConfig } from '../../lib/types.js';
import { StepList } from './StepList.js';
import { PendingStep } from './PendingStep.js';
import { BuilderHelper } from '../builderHelper/BuilderHelper.js';
import { ReadOnlySteps } from './ReadOnlySteps.js';
import { TriggerPicker } from './TriggerPicker.js';
import { TestTab } from './TestTab.js';
import { SummarySentence } from './SummarySentence.js';
import { summarize } from './summarize.js';
import { useBuilderEditorLogic } from './useBuilderLogic.js';

/**
 * Built-in workflows: same chrome, same step vocabulary as a composed one —
 * the only differences are that the steps are fixed and the one configurable
 * slot (who approves) expands inline on its own step.
 */
/**
 * The server's issue strings carry a breadcrumb — "step 4 → Needs a mechanic:
 * step 1 → Approved: say what the agent should investigate" — which is right but
 * unreadable stacked in a rail. Split it: the ask reads as the line, the place
 * sits under it quietly.
 */
const splitIssue = (issue: string): { where: string | null; what: string } => {
  const at = issue.lastIndexOf(': ');
  if (at === -1) return { where: null, what: issue };
  const where = issue.slice(0, at);
  // Only a real breadcrumb — a colon inside the sentence isn't one.
  if (!/step \d|“/.test(where)) return { where: null, what: issue };
  return { where, what: issue.slice(at + 2) };
};

const RUN_DOT: Record<string, string> = {
  done: 'bg-success',
  running: 'bg-info',
  suspended: 'border-2 border-warning',
  failed: 'bg-danger',
};
const RUN_WORD: Record<string, string> = {
  done: 'Finished',
  running: 'Running',
  suspended: 'Waiting on someone',
  deferred: 'Paused — daily limit',
  failed: 'Couldn’t finish',
};

const SystemView = ({ logic }: { logic: ReturnType<typeof useBuilderEditorLogic> }) => {
  const w = logic.workflow;
  const config = (w?.systemConfig ?? {}) as HitlRouteConfig;
  const [approvers, setApprovers] = useState<ApproverRef[]>(config.hitl?.approvers ?? []);
  if (!w) return null;
  return (
    <AppShell>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="text-xl font-semibold">{w.name}</h1>
          <Badge variant={w.enabled ? 'success' : 'neutral'}>
            {w.enabled ? 'Enabled' : 'Paused'}
          </Badge>
          <span className="text-xs text-ink-subtle">Built in — steps are fixed</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" asChild>
            <Link to={`/settings/workflows/${w.id}/runs`}>View history</Link>
          </Button>
          <Button variant="secondary" onClick={() => logic.setEnabled(!w.enabled)}>
            {w.enabled ? 'Pause' : 'Resume'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_19rem]">
        <Card padding="md" className="min-w-0">
          <div className="mb-4 flex min-w-0 flex-wrap items-center gap-2 rounded-lg border border-outline bg-surface-elevated/60 px-3 py-2 text-sm">
            <span className="text-warning">⚡</span>
            <span className="text-ink-muted">When</span>
            <code className="rounded border border-outline bg-surface px-1.5 py-0.5 text-xs">
              {w.trigger}
            </code>
            <span className="text-xs text-ink-subtle">arrives</span>
          </div>
          <ReadOnlySteps
            steps={w.systemSteps ?? []}
            branches={w.systemBranches}
            slot={
              w.hasConfig ? (
                <div className="grid gap-2">
                  <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-ink-subtle">
                    Who can approve
                  </span>
                  <Select
                    aria-label="Approvers"
                    multiple
                    options={logic.approverCandidates.map((c) => ({
                      value: `${c.kind}:${c.id}`,
                      label: c.eligible ? c.label : `${c.label} (${c.reason ?? 'ineligible'})`,
                    }))}
                    value={approvers.map((a) => `${a.kind}:${a.id}`)}
                    onChange={(v) =>
                      setApprovers(
                        v.map((s) => {
                          const [kind, id] = s.split(':');
                          return { kind: kind as 'user' | 'group', id: id ?? '' };
                        }),
                      )
                    }
                  />
                  <Button
                    className="justify-self-start"
                    variant="secondary"
                    disabled={logic.savingSystemConfig}
                    onClick={() => logic.saveSystemConfig({ hitl: { enabled: true, approvers } })}
                  >
                    {logic.savingSystemConfig ? 'Saving…' : 'Save approvers'}
                  </Button>
                </div>
              ) : null
            }
          />
        </Card>
        <Card padding="md" className="min-w-0 self-start">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
            About this workflow
          </h2>
          <p className="text-xs text-ink-muted">
            SC builds and maintains this one. You choose whether it runs and who approves; its steps
            ship with the platform.
          </p>
        </Card>
      </div>
    </AppShell>
  );
};

/**
 * The rail: what's left before this can run, how it has been going, and the
 * technical reference tucked where someone can find it when they need to hand
 * it to whoever connects the sending system.
 */
const EditorRail = ({ logic }: { logic: ReturnType<typeof useBuilderEditorLogic> }) => {
  const issues = logic.workflow?.issues ?? [];
  const isPlatformTrigger = logic.platformTriggers.some((t) => t.type === logic.trigger);
  const ready = !logic.isNew && issues.length === 0 && logic.trigger !== '';
  return (
    <div className="grid min-w-0 gap-4 self-start">
      <Card padding="md" className="min-w-0">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Before you turn this on
        </h2>
        <ul className="grid gap-2.5">
          <li className="flex items-start gap-2 text-xs text-ink-muted">
            {logic.trigger ? (
              <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-success" aria-hidden />
            ) : (
              <CircleAlert className="mt-0.5 h-3.5 w-3.5 flex-none text-warning" aria-hidden />
            )}
            <span>
              {logic.trigger ? 'Something starts it' : 'Nothing starts it yet — choose an event'}
            </span>
          </li>
          {issues.map((issue) => {
            const { where, what } = splitIssue(issue);
            return (
              <li key={issue} className="flex items-start gap-2 text-xs text-ink-muted">
                <CircleAlert className="mt-0.5 h-3.5 w-3.5 flex-none text-warning" aria-hidden />
                <span className="min-w-0">
                  <span className="block">{what}</span>
                  {where !== null && (
                    <span className="block truncate text-[0.68rem] text-ink-subtle" title={where}>
                      {where}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
          {ready && (
            <li className="flex items-start gap-2 text-xs text-ink-muted">
              <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-success" aria-hidden />
              <span>Every step is filled in</span>
            </li>
          )}
        </ul>
      </Card>

      <Card padding="md" className="min-w-0">
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input
            type="checkbox"
            className="accent-primary"
            checked={logic.definition.createCase ?? false}
            onChange={(event) => logic.setCreateCase(event.target.checked)}
          />
          Create a case
        </label>
        <p className="mt-2 text-xs text-ink-muted">
          Save the event in a case when this workflow starts, even if no notification is sent.
          People in your organization can open it from run history or a notification. Workflows
          responding to the same event share its case.
        </p>
      </Card>

      {/* Only once there is something to say. A card reading "it hasn't run
          yet", above a link to an empty history, is furniture — the editor
          already makes it obvious this is a draft. */}
      {!logic.isNew && logic.recentRuns.length > 0 && (
        <Card padding="md" className="min-w-0">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">
              Lately
            </h2>
            <Link
              to={`/settings/workflows/${logic.workflow?.id ?? ''}/runs`}
              className="text-xs text-primary hover:underline"
            >
              See all
            </Link>
          </div>
          <ul className="grid gap-2">
            {logic.recentRuns.map((run) => (
              <li key={run.id} className="flex items-center gap-2 text-xs">
                <span
                  aria-hidden
                  className={`h-2 w-2 flex-none rounded-full ${RUN_DOT[run.status] ?? 'bg-outline-strong'}`}
                />
                <span className="flex-1 text-ink-muted">{RUN_WORD[run.status] ?? run.status}</span>
                <span className="text-ink-subtle">
                  {new Date(run.startedAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {logic.trigger !== '' && (
        <Card padding="md" className="min-w-0">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
            Event ID
          </h2>
          <code className="block truncate rounded border border-outline bg-surface px-1.5 py-1 text-[0.68rem] text-ink-muted">
            {logic.trigger}
          </code>
          {/* Who needs this depends on where the event comes from: for a
              tenant's own event it's the person wiring up the sender; for a
              platform event nobody is wiring anything, so it's just the name
              to look for in Events. */}
          <p className="mt-1.5 text-xs text-ink-subtle">
            {isPlatformTrigger
              ? 'What these are called on the Events page.'
              : 'Your system puts this on the events it sends. Give it to whoever sets that up.'}
          </p>
        </Card>
      )}
    </div>
  );
};

/**
 * What the header says instead of a Save button: a draft that isn't enabled
 * can't do anything, so there is nothing for a person to decide — only
 * something for them to be told.
 */
const SaveState = ({ logic }: { logic: ReturnType<typeof useBuilderEditorLogic> }) => {
  if (logic.isNew) return null;
  if (logic.saving) return <span className="text-xs text-ink-subtle">Saving…</span>;
  if (logic.dirty) return <span className="text-xs text-ink-subtle">Unsaved changes…</span>;
  if (logic.savedAt === null) return null;
  return (
    <span className="flex items-center gap-1.5 text-xs text-ink-subtle">
      <Check className="h-3.5 w-3.5" aria-hidden />
      Saved
    </span>
  );
};

export const BuilderEditorPage = () => {
  const { workflowId } = useParams();
  const logic = useBuilderEditorLogic(workflowId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState<'build' | 'test'>('build');
  // The tab exists only when the workflow HAS a report step (anywhere in the
  // tree) — a design without a producer is noise.
  // Handed over from the gallery's "Describe it": read once as initial state,
  // so closing the panel doesn't fight the URL.
  const [params] = useSearchParams();
  const [helperOpen, setHelperOpen] = useState(params.get('chat') === '1');
  // The builder says something is coming while the helper is thinking — the
  // long wait is the model, not the reveal that follows it.
  const [chatBusy, setChatBusy] = useState(false);
  if (logic.isLoading) {
    return (
      <AppShell>
        <p className="text-sm text-ink-muted">Loading…</p>
      </AppShell>
    );
  }
  if (!logic.isNew && !logic.workflow) {
    return (
      <AppShell>
        <Banner variant="warning">Workflow not found.</Banner>
      </AppShell>
    );
  }
  if (logic.workflow?.kind === 'system') return <SystemView logic={logic} />;

  const enabled = logic.enabled;
  return (
    <AppShell>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link
          to="/settings/workflows"
          aria-label="Back to workflows"
          className="flex items-center text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        {/* The name edits in place — a bordered box in the page title slot
            reads as a form to fill in rather than a thing that has a name. */}
        <div className="flex min-w-0 items-center gap-2 border-b border-dashed border-outline-strong pb-0.5">
          <Input
            aria-label="Workflow name"
            value={logic.displayName}
            placeholder="Name this workflow"
            onChange={(e) => logic.setDisplayName(e.target.value)}
            className="w-72 border-0 bg-transparent px-0 text-xl font-semibold focus-visible:ring-0"
          />
          <Pencil className="h-3.5 w-3.5 flex-none text-ink-subtle" aria-hidden />
        </div>
        <div className="flex-1" />
        <SaveState logic={logic} />
        {!logic.isNew && (
          <Button variant="secondary" asChild>
            <Link to={`/settings/workflows/${workflowId ?? ''}/runs`}>History</Link>
          </Button>
        )}
        {logic.isNew ? (
          <Button onClick={logic.save} disabled={logic.saving}>
            {logic.saving ? 'Creating…' : 'Create draft'}
          </Button>
        ) : (
          <Toggle
            checked={enabled}
            onChange={() => logic.setEnabled(!enabled)}
            label={`${logic.displayName || 'This workflow'} on`}
          />
        )}
      </div>
      {logic.saveError !== null && (
        <Banner variant="danger" className="mb-3">
          {logic.saveError instanceof Error ? logic.saveError.message : 'Save failed.'}
        </Banner>
      )}
      {logic.enableError !== null && (
        <Banner variant="warning" className="mb-3">
          {logic.enableError instanceof Error
            ? logic.enableError.message
            : 'Couldn’t turn this on yet.'}
        </Banner>
      )}

      {!logic.isNew && (
        <div className="mb-4 flex gap-1 border-b border-outline" role="tablist">
          {(
            [
              { id: 'build', label: 'Build' },
              { id: 'test', label: 'Test' },
            ] as { id: 'build' | 'test'; label: string }[]
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'border-primary text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'test' && !logic.isNew ? (
        <TestTab logic={logic} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
          <div className="grid min-w-0 gap-4">
            {/* A preview over a saved workflow has to be reversible, or it is
                just an edit nobody agreed to. Nothing here is written until
                the proposal is applied. */}
            {logic.previewing && (
              <Banner variant="info">
                <span className="flex-1">
                  Showing the helper&apos;s suggested changes — nothing is saved yet.
                </span>
                <button
                  type="button"
                  onClick={logic.discardPreview}
                  className="flex-none font-medium underline underline-offset-2"
                >
                  Put it back
                </button>
              </Banner>
            )}
            {/* Read before edited: the whole definition as one sentence, so a
              wrong branch is caught here rather than in a run. */}
            <Card padding="md" className="min-w-0">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
                In plain English
              </h2>
              <SummarySentence
                className="text-base leading-relaxed text-ink-muted"
                tokens={summarize(logic.trigger, logic.definition, {
                  triggers: logic.platformTriggers,
                  schedule: logic.schedule,
                  destinationLabel: (id) =>
                    logic.destinationOptions.find((d) => d.value === id)?.label,
                  // "Ada — ada@acme.com" is the picker's disambiguator, not a
                  // name to read in the middle of a sentence.
                  approverLabel: (ref) =>
                    logic.approverCandidates
                      .find((c) => c.kind === ref.kind && c.id === ref.id)
                      ?.label.split(' — ')[0],
                })}
              />
            </Card>
            <Card padding="md" className="min-w-0">
              <div className="mb-4 min-w-0 rounded-lg border border-outline bg-surface-elevated/60 px-3 py-2 text-sm">
                <div className="flex min-w-0 items-start gap-2">
                  <span className="pt-0.5 text-warning">⚡</span>
                  <TriggerPicker
                    isNew={logic.isNew}
                    value={logic.trigger}
                    onChange={logic.setTrigger}
                    triggers={logic.platformTriggers}
                    schedule={logic.schedule}
                    onScheduleChange={logic.setSchedule}
                  />
                </div>
                {!logic.isNew && (
                  <p className="mt-1.5 pl-6 text-xs text-ink-subtle">
                    Changing this affects future runs only. Any value below that the new event
                    doesn’t carry turns red — replace those, then run a test.
                  </p>
                )}
              </div>

              {/* Where the next step is about to land. It is theatre — the
                definition already arrived whole — so it is bounded, skipped
                under reduced-motion, and never claims a percentage. */}
              <StepList
                definition={logic.definition}
                expandedPath={logic.expandedPath}
                onToggle={logic.onToggle}
                eventSource={logic.eventSource}
                destinationOptions={logic.destinationOptions}
                peopleOptions={logic.peopleOptions}
                approverCandidates={logic.approverCandidates}
                stepTypes={logic.stepTypes}
                personas={logic.personas}
                triggerFields={logic.triggerFields}
                {...logic.stepEdit}
                pending={
                  logic.revealing || chatBusy ? (
                    <div aria-live="polite">
                      <PendingStep
                        index={logic.definition.steps.length + 1}
                        label={
                          logic.revealing ? 'Writing this step…' : 'Working out what comes next…'
                        }
                      />
                    </div>
                  ) : null
                }
              />
            </Card>
          </div>

          <div className="grid min-w-0 gap-4 self-start">
            <EditorRail logic={logic} />
            {!logic.isNew && !enabled && (
              // Reads as destructive before it's clicked: the danger family
              // throughout, and the button is the design system's outlined
              // danger variant, which fills red on hover.
              <Card padding="md" className="min-w-0 border-danger-border bg-danger-soft">
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-danger">
                  Danger zone
                </h2>
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                  Delete workflow
                </Button>
              </Card>
            )}
            {/* Kept mounted and driven by `open` — conditional mounting skips the
              modal's own focus/backdrop lifecycle. */}
            <Modal open={confirmDelete} onOpenChange={setConfirmDelete}>
              <Modal.Header>
                <Modal.Title>Delete this workflow?</Modal.Title>
                <Modal.CloseButton />
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-ink-muted">
                  “{logic.displayName || 'Untitled workflow'}” will be removed for everyone. Its
                  past runs stay in the history, but the workflow itself can't be brought back —
                  you'd have to build it again.
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                  Keep it
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setConfirmDelete(false);
                    logic.deleteWorkflow();
                  }}
                >
                  Delete workflow
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
      )}
      {/* The helper fills the draft on screen — a new one it just described,
          or a revision of the one being edited. Either way the person saves
          it themselves; the helper never writes a workflow of its own. */}
      <BuilderHelper
        trigger={logic.trigger || undefined}
        definition={logic.definition}
        onApply={logic.applyProposal}
        onDraftPreview={logic.previewDraft}
        onBusyChange={setChatBusy}
        open={helperOpen}
        onOpenChange={setHelperOpen}
      />
    </AppShell>
  );
};
